import type { FC } from "react";

export interface WakeDeliverySummary {
  addressedCount?: number;
  claimedCount?: number;
  oldestUnclaimedAge?: number | null;
}

interface WakeDeliveryChipProps {
  summary: WakeDeliverySummary | null;
}

export const WakeDeliveryChip: FC<WakeDeliveryChipProps> = ({ summary }) => {
  if (summary === null) {
    return null;
  }
  const addressedCount = summary.addressedCount ?? 0;
  const claimedCount = summary.claimedCount ?? 0;
  const oldestUnclaimedAge = summary.oldestUnclaimedAge ?? null;
  if (addressedCount === 0) {
    return null;
  }
  const stranded = claimedCount < addressedCount;
  const tone = wakeChipTone(stranded, oldestUnclaimedAge);
  const toneClass = WAKE_TONE_CLASS[tone];
  const ageLabel =
    oldestUnclaimedAge !== null && stranded
      ? ` · oldest ${formatStrandedAge(oldestUnclaimedAge)}`
      : "";
  return (
    <span
      title={
        stranded
          ? `${addressedCount - claimedCount} addressed actor(s) have not claimed their wake.`
          : "All addressed actors claimed the latest wake."
      }
      className={`inline-flex items-center gap-1 border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.16em] ${toneClass}`}
    >
      <span>
        WAKES {claimedCount}/{addressedCount}
      </span>
      {ageLabel && <span>{ageLabel}</span>}
    </span>
  );
};

type WakeTone = "ok" | "warning" | "danger";

const WAKE_TONE_CLASS: Record<WakeTone, string> = {
  danger: "border-rose-500/50 bg-rose-500/10 text-rose-700 dark:text-rose-300",
  ok: "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300",
  warning:
    "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
};

function wakeChipTone(
  stranded: boolean,
  oldestUnclaimedAge: number | null,
): WakeTone {
  if (!stranded) {
    return "ok";
  }
  if (oldestUnclaimedAge !== null && oldestUnclaimedAge > 60) {
    return "danger";
  }
  return "warning";
}

function formatStrandedAge(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m`;
  }
  return `${Math.floor(seconds / 3600)}h`;
}
